// Small pure-function utilities + shared lookup tables. Anything
// here should be UI-framework-agnostic so it stays cheap to import
// from anywhere.

// Maps an AuditLog row's `event_type` to a display label + a Badge
// variant. Shared between the AuditLogPage (full table) and
// MatterActivityCard (inline recent-activity widget). Unknown event
// types fall back to {label: event_type, variant: "default"} at the
// call site.
export const EVENT_TYPE_CONFIG = {
  doc_generated: { label: "Doc Generated", variant: "gold" },
  doc_uploaded: { label: "Doc Uploaded", variant: "gold" },
  extraction_classified: { label: "Extraction", variant: "navy" },
  extraction_pushed: { label: "Extraction Pushed", variant: "navy" },
  intake_summary: { label: "Intake Summary", variant: "navy" },
  matter_created: { label: "Matter Created", variant: "success" },
  contact_created: { label: "Contact Created", variant: "success" },
  provider_added: { label: "Provider Added", variant: "default" },
  provider_removed: { label: "Provider Removed", variant: "default" },
  task_created: { label: "Task Created", variant: "gold" },
  auth_login: { label: "Login", variant: "navy" },
  custom_fields_set: { label: "Fields Set", variant: "default" },
  // Read events, not writes. The filter dropdown is built from these
  // keys, so an event type missing here is an event nobody can search
  // for — which for PHI access defeats the point of recording it.
  // Neutral variant on purpose: a read is normal activity, not an alert.
  phi_read: { label: "PHI Viewed", variant: "navy" },
};

/**
 * Format an ISO 8601 timestamp string for display in audit-log
 * entries.
 *
 * - When `relative` is true, returns a "just now / Nm ago / Nh ago /
 *   Nd ago" string for events less than ~7 days old, and an absolute
 *   timestamp for anything older.
 * - When `relative` is false, always returns an absolute timestamp.
 *
 * Defensive about the "missing trailing Z" foot-gun — the backend
 * sometimes writes audit timestamps without a timezone marker; the
 * append-Z-if-needed shim normalizes everything to UTC before
 * formatting so users in different time zones see consistent values.
 */
export function formatAuditTime(isoStr, relative) {
  if (!isoStr) return "";
  // Substep 4-ν sidecar: append "Z" ONLY when the string has neither
  // a Z suffix nor a numeric timezone offset (+HH:MM or -HH:MM).
  // Before this fix, tz-aware timestamps from Postgres (like
  // "2026-08-15T20:14:22+00:00") were getting a "Z" appended and
  // becoming invalid ("...+00:00Z"), which produced "Invalid Date"
  // in the audit log rendering.
  const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(isoStr);
  const d = new Date(isoStr + (hasTz ? "" : "Z"));
  if (relative) {
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return diffMin + "m ago";
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return diffHr + "h ago";
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return diffDay + "d ago";
  }
  return d.toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" })
    + " " + d.toLocaleTimeString("en-US", { hour:"numeric", minute:"2-digit" });
}
